// eslint-disable-next-line jira/filename-case
import React, { useCallback, useMemo, type ComponentType } from 'react';
import { AiRovoCurrentRouteConfigurationProviderWithBoundary } from '@jira/ai-rovo_actions-route-provider/src/AiRovoCurrentRouteConfigurationProviderWithBoundary.tsx';
import {
	projectTypeToCanonicalId,
	toProjectType,
} from '@jira/platform__project-types/src/project-types.tsx';
import { SafeEmbeddedConfluencePanel } from '@jira/issue__create-confluence-content/src/SafeEmbeddedConfluencePanel.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { isStableUIEnabledForTests } from '@jira/platform__is-stable-ui-enabled-for-tests/src/isStableUIEnabledForTests.tsx';
import { PageDwellAnalyticsListener } from '@jira/platform__page-dwell-analytics-listener/src/index.tsx';
import ProjectKeyRedirect from '@jira/platform__spa/src/services/project-key-redirect/index.tsx';
import { AsyncUpflowIframeComponent } from '@jira/growth__up-flow-upflow-iframe-plugin/src/async.tsx';
import { JiraHeritageLoader } from '@jira/platform__wrm/src/jira-heritage-loader/index.tsx';
import { AgentPresenceHeartbeatLazy } from '@jira/servicedesk__agent-presence-heartbeat/src/AgentPresenceHeartbeatLazy.tsx';
import CacheRegistry from '@jira/platform__cache/src/ui/index.tsx';
import { SafeConversationAssistantPanel } from '@jira/platform__conversation-assistant/src/SafeConversationAssistantPanel.tsx';
import {
	AsyncDeferEditionEPCheckWrapper,
	AsyncDeferEditionEPCheckWrapperNew,
} from '@jira/growth__defer-edition-modal/async.tsx';
import { jiraDeferEditionModalEntryPoint } from '@jira/growth__defer-edition-modal/src/jiraDeferEditionModalEntryPoint.entrypoint.tsx';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import EventDelayController from '@jira/platform__observability-event-delay-controller/src/index.tsx';
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component/src/index.tsx';
import { PriorityManager } from '@jira/platform__services-non-critical/src/PriorityManager.tsx';
import { renderInBackgroundPriority } from '@jira/platform__services-non-critical/src/BackgroundPriority.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { SafeHelpPanel } from '@jira/platform__help-panel-right-sidebar/src/index.tsx';
import { ImperativeAPI } from '@jira/platform__legacy-imperative-api/src/index.tsx';
import { useProject } from '@jira/navigation-apps__sidebar-common/src/controllers/project/index.tsx';
import { MatchedRouteListener } from '@jira/platform__utils-route-analytics/src/ui/matched-route-listener/index.tsx';
import { PreviousRouterStateListener } from '@jira/platform__utils-route-analytics/src/ui/previous-router-state-listener/index.tsx';
import QueryStringNotifier from '@jira/platform__services-querystring-notifier/src/index.tsx';
import { AsyncRecommendPremiumToStandardModalWrapper as AsyncRecommendPremiumToStandardModal } from '@jira/growth__recommend-premium-to-standard-modal/src/async.tsx';
import {
	createLayout,
	type Layout,
} from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { lazy, lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { CrossJoinNudges } from '@jira/growth__cross-join-nudges/src/ui/CrossJoinNudges.tsx';
import { AsyncSSRFailureDisplay } from '@jira/ssr__rendering-guard/src/ssr-failure-display/async.tsx';
import { JiraCommandPaletteLazy } from '@jira/compal__command-palette-jira-integration/src/async.tsx';
import { BreadcrumbAdapter } from './ui/breadcrumbs/BreadcrumbAdapter.tsx';
import { LoadingPhaseManager } from './ui/loading-phase-manager/index.tsx';
import { TrackClicksBeforeTTI } from './ui/track-clicks-before-tti/index.tsx';
import { Integrations } from './ui/integrations/index.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const Global = lazy(
	() => import(/* webpackChunkName: "async-global-global-components" */ './ui/global/index.tsx'),
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const Navigation = lazyAfterPaint(
	() =>
		import(
			/* webpackChunkName: "async-global-navigation-components" */ './ui/navigation/index.tsx'
		),
	{
		ssr: false,
	},
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const Trackers = lazyAfterPaint(
	() =>
		import(/* webpackChunkName: "async-global-trackers-components" */ './ui/trackers/index.tsx'),
	{
		ssr: false,
	},
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const RemoteActions = lazyAfterPaint(
	() => import(/* webpackChunkName: "async-remote-actions" */ './ui/remote-actions/index.tsx'),
	{
		ssr: false,
	},
);

// No need for a whole chunk for a single component
// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const AfterPaintIssuePreEval = lazyAfterPaint(
	/* eslint-disable-next-line jira/import/webpack-magic-comment-entries */
	() => import('./ui/after-paint-issue-preeval/AfterPaintIssuePreEval.tsx'),
	{
		ssr: false,
	},
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const AsyncBrowserStorageControlsInit = lazy(() =>
	import(
		/* webpackChunkName: "async-browser-storage-controls-init" */ './ui/browser-storage-controls-init/index.tsx'
	).then(({ BrowserStorageControlsInit }) => BrowserStorageControlsInit),
);

const runtimeProps = {};

export const DeferEditionModalEntryPoint: {
	(): React.JSX.Element;
	displayName: string;
} = (): React.JSX.Element => {
	const cloudID = useCloudId();
	const { data: project } = useProject();
	const projectType = toProjectType(project?.type) || null;
	const productKey = projectTypeToCanonicalId(projectType);

	const deferEditionModalEntryPointParams = useMemo(
		() => ({
			cloudId: cloudID,
			productKey,
		}),
		[cloudID, productKey],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		jiraDeferEditionModalEntryPoint,
		deferEditionModalEntryPointParams,
	);

	const onComplete = useCallback(() => {
		entryPointActions.load();
	}, [entryPointActions]);

	return (
		<>
			{fg('ttai_fix_defer_edition_modal') ? (
				<AsyncDeferEditionEPCheckWrapperNew onComplete={onComplete}>
					<JiraEntryPointContainer
						id="growth.jiraDeferEditionModal"
						packageName="jira-defer-edition-modal"
						entryPointReferenceSubject={entryPointReferenceSubject}
						fallback={null}
						runtimeProps={runtimeProps}
					/>
				</AsyncDeferEditionEPCheckWrapperNew>
			) : (
				<AsyncDeferEditionEPCheckWrapper onComplete={onComplete}>
					<JiraEntryPointContainer
						id="growth.jiraDeferEditionModal"
						packageName="jira-defer-edition-modal"
						entryPointReferenceSubject={entryPointReferenceSubject}
						fallback={null}
						runtimeProps={runtimeProps}
					/>
				</AsyncDeferEditionEPCheckWrapper>
			)}
		</>
	);
};

const AgentPresenceHeartbeatInBackground = renderInBackgroundPriority(AgentPresenceHeartbeatLazy);
const ConditionalAgentPresenceHeartbeat = componentWithFG(
	'jsm_wfo_agent_presence',
	AgentPresenceHeartbeatInBackground,
	() => null,
);
const RemoteActionsInBackground = renderInBackgroundPriority(RemoteActions);
const ConditionalRemoteActions = componentWithFG(
	'ttai_fix_remoteactions_and_browserstoragecontrols',
	RemoteActionsInBackground,
	RemoteActions,
);

const MaybeAsyncBrowserStorageControlsInit = () => {
	if (
		// @ts-expect-error - __hasStorageControlsBeenInit is set on globalThis by the early entry script.
		globalThis?.__hasStorageControlsBeenInit
	) {
		return null;
	}
	return <AsyncBrowserStorageControlsInit />;
};

DeferEditionModalEntryPoint.displayName = 'DeferEditionModal';
ConditionalAgentPresenceHeartbeat.displayName = 'ConditionalAgentPresenceHeartbeat';
ConditionalRemoteActions.displayName = 'Lazy(ui/remote-actions/index.tsx)';

const clientOnlyGlobalComponents: ComponentType<{}>[] = [
	PriorityManager,
	MatchedRouteListener,
	PreviousRouterStateListener,
	LoadingPhaseManager,
	CacheRegistry,
	BreadcrumbAdapter,
	QueryStringNotifier,
	PageDwellAnalyticsListener,
	ImperativeAPI,
	TrackClicksBeforeTTI,
	ProjectKeyRedirect,
	AsyncUpflowIframeComponent,
	JiraHeritageLoader,
	MaybeAsyncBrowserStorageControlsInit,
	CrossJoinNudges,
	EventDelayController,
	ConditionalRemoteActions,
	DeferEditionModalEntryPoint,
	AsyncRecommendPremiumToStandardModal,
	AsyncSSRFailureDisplay,
	// Integrations - Issue View Modal, Create Project Drawer & Create dashboard modal
	Integrations,
	ConditionalAgentPresenceHeartbeat,
];

const getSharedGlobalComponents = (components: ComponentType<{}>[]) => {
	if (__SERVER__) {
		return [];
	}
	return components;
};

const sharedGlobalComponents: ComponentType<{}>[] = [
	...getSharedGlobalComponents(clientOnlyGlobalComponents),
	Global,
	Navigation,
	Trackers,
	renderInBackgroundPriority(AfterPaintIssuePreEval),
	AiRovoCurrentRouteConfigurationProviderWithBoundary,
];

const useRouteIssueKey = createRouterSelector(({ match }) =>
	typeof match?.params?.issueKey === 'string' ? match.params.issueKey : undefined,
);

const useNoIssueKey = (): string | undefined => undefined;
const useSafeEmbeddedConfluencePanelIssueKey = functionWithCondition(
	() => fg('remove-issue-context-mirror-on-wiv'),
	useRouteIssueKey,
	useNoIssueKey,
);

/**
 * Layout-level adapter for the embedded Confluence side-panel.
 *
 * The shared layout owns the right-sidebar slot, so the panel is mounted
 * **outside** any IssueView / WorkItemView provider tree. Instead of relying on
 * IssueContextContainer being mirrored up via an `IssueContextRepeater`, we
 * resolve the current `issueKey` from the route here and hand it to the panel
 * as a prop. The panel then scopes its sweet-state container by this value
 * without touching `useIssueKey()`.
 *
 * Route params used here are the same `:issueKey` segment used by every Jira
 * issue/work-item route definition (see router-routes/*).
 */
const SafeEmbeddedConfluencePanelInLayout = (): React.JSX.Element => {
	const issueKey = useSafeEmbeddedConfluencePanelIssueKey();
	return <SafeEmbeddedConfluencePanel issueKey={issueKey} />;
};

export const sharedLayout: Layout = createLayout({
	rightSidebars: [
		SafeHelpPanel,
		SafeConversationAssistantPanel,
		SafeEmbeddedConfluencePanelInLayout,
	],
	globalComponents: sharedGlobalComponents,
	preloadComponents: [RemoteActions, JiraCommandPaletteLazy, AsyncBrowserStorageControlsInit],
});

// "minimal" here means the client-only global components that are required
// to be rendered when the app is in "stable-ui" mode. When not included these cause the following issues:
// LoadingPhaseManager: UFO events are not fired.
// CacheRegistry: feature-gate/experiment overrides are not persisted in local dev.
const minimalClientOnlyGlobalComponents: ComponentType<{}>[] = [LoadingPhaseManager, CacheRegistry];
const minimalGlobalComponents: ComponentType<{}>[] = getSharedGlobalComponents(
	minimalClientOnlyGlobalComponents,
);
const minimalLayout: Layout = createLayout({
	rightSidebars: [],
	globalComponents: minimalGlobalComponents,
	preloadComponents: [],
});

export const getSharedLayout = (): Layout => {
	if (fg('jpx-870-stable-ui-for-criterion')) {
		if (isStableUIEnabledForTests()) {
			return minimalLayout;
		}
	}
	return sharedLayout;
};
